import type { KeyboardShortcutId } from '@getgo/container-client'
import { asTranslationKey } from '../../common/translate-helpers/i18n-utils'
import { isMac } from '../../services/container/helpers'
import type { KeyboardShortcutConfigObject } from '../../services/keyboard-shortcut/keyboard-shortcut'

export interface HotkeyUIStructureSection {
  readonly sectionLabelTranslated: string
  readonly keyboardShortcutIdListShown: ReadonlyArray<KeyboardShortcutId>
}

// these shortcuts are only to be displayed in the UI, but should not be editable by the user
export const nonEditableShortcuts: Partial<KeyboardShortcutConfigObject> = {
  openMenuBarWindowsOnly: [
    {
      keyCombination: {
        code: 'F10',
        altKey: false,
        ctrlKey: false,
        metaKey: false,
        shiftKey: false,
      },
      global: false,
    },
    {
      keyCombination: {
        code: 'AltLeft',
        altKey: true,
        ctrlKey: false,
        metaKey: false,
        shiftKey: false,
      },
      global: false,
    },
  ],
}

const macOsDefaultUIConfiguration: ReadonlyArray<HotkeyUIStructureSection> = [
  {
    sectionLabelTranslated: asTranslationKey('Main Shortcuts'),
    keyboardShortcutIdListShown: [
      'newItem',
      'toggleMute',
      'toggleCamera',
      'leaveMeetingOrHangUpCall',
      'search',
      'jumpTo',
      'callCurrentContact',
      'openSoftphone',
      'openSettings',
    ],
  },
  {
    sectionLabelTranslated: asTranslationKey('Call notifications'),
    keyboardShortcutIdListShown: ['answerCall', 'declineCall'],
  },
  {
    sectionLabelTranslated: asTranslationKey('Navigation'),
    keyboardShortcutIdListShown: [
      'showPhone',
      'showInbox',
      'showMeeting',
      'showContact',
      'showContactCenter',
      'showAnalytics',
    ],
  },
  {
    sectionLabelTranslated: asTranslationKey('Meetings'),
    keyboardShortcutIdListShown: [
      'showMeetingParticipants',
      'showMeetingChatMessages',
      'showSettings',
      'showNotes',
      'toggleViewMode',
    ],
  },
  {
    sectionLabelTranslated: asTranslationKey('Phone'),
    keyboardShortcutIdListShown: ['holdCall', 'transferCall'],
  },
]

const windowDefaultUIConfiguration: ReadonlyArray<HotkeyUIStructureSection> = [
  {
    sectionLabelTranslated: asTranslationKey('Main Shortcuts'),
    keyboardShortcutIdListShown: [
      'newItem',
      'toggleMute',
      'toggleCamera',
      'leaveMeetingOrHangUpCall',
      'search',
      'jumpTo',
      'callCurrentContact',
      'openSoftphone',
      'openSettings',
      'openMenuBarWindowsOnly',
    ],
  },
  {
    sectionLabelTranslated: asTranslationKey('Call notifications'),
    keyboardShortcutIdListShown: ['answerCall', 'declineCall'],
  },
  {
    sectionLabelTranslated: asTranslationKey('Navigation'),
    keyboardShortcutIdListShown: [
      'showPhone',
      'showInbox',
      'showMeeting',
      'showContact',
      'showContactCenter',
      'showAnalytics',
    ],
  },
  {
    sectionLabelTranslated: asTranslationKey('Meetings'),
    keyboardShortcutIdListShown: [
      'showMeetingParticipants',
      'showMeetingChatMessages',
      'showSettings',
      'showNotes',
      'toggleViewMode',
    ],
  },
  {
    sectionLabelTranslated: asTranslationKey('Phone'),
    keyboardShortcutIdListShown: ['holdCall', 'transferCall'],
  },
]

export const KeyboardShortcutUIStructureProvider = {
  get: (): ReadonlyArray<HotkeyUIStructureSection> =>
    isMac() ? macOsDefaultUIConfiguration : windowDefaultUIConfiguration,
}
